import React, { ComponentProps, forwardRef, useEffect } from 'react'
import { useDebounce } from 'react-use'
import compact from 'lodash/compact'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import {
  TInputChangeHandler,
  useAutocompleteInputState,
  useAutocompleteLazyQuery,
} from 'transfix-ui/components/AutocompleteBase/hooks'
import {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
} from 'transfix-ui/components/AutocompleteBase/types'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'
import { useDefaultUserQuery } from './graphql/DefaultUser'
import { ISearchGroupFragment } from './graphql/SearchGroupFragment'
import { ISearchUserFragment } from './graphql/SearchUserFragment'
import { useUsersLazyQuery } from './graphql/Users'

type TGroup = 'User' | 'Group'
interface IUser {
  id: string
  type: TGroup
}

const groupNameToType: Record<string, TGroup> = {
  Users: 'User',
  Groups: 'Group',
}

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: string | (string | IUser)[] | null
): IAutocompleteBaseOption | IAutocompleteBaseOption[] | null => {
  if (!value) return null

  if (typeof value === 'string') {
    const option = options.find(({ id }) => id === value)
    return option || null
  }

  const selectedOptions = options.filter(({ groupName, id }) => {
    return value?.find(v => {
      if (typeof v === 'string') {
        return id === v
      }

      const { id: valId, type } = v
      return valId === id && groupNameToType[groupName || ''] === type
    })
  })

  return selectedOptions || null
}

export interface IAutocompleteUserProps
  extends Omit<
    ComponentProps<typeof AutocompleteBase>,
    'value' | 'onChange' | 'options' | 'loading'
  > {
  value: string | string[] | IUser[] | null
  required?: boolean
  onChange: (newVal: string | string[] | IUser | IUser[] | null) => void
  inputError?: boolean
  inputValidationMessage?: string
  dataTest?: string
  placeholder?: string
  usersOnly?: boolean
}

export const AutocompleteUser = forwardRef<HTMLInputElement, IAutocompleteUserProps>(
  (
    {
      autoSelect = true,
      clearOnBlur = false,
      dataTest = 'AutocompleteUser',
      inputError,
      inputIcon = 'User',
      inputValidationMessage,
      multiple,
      onChange,
      renderInputType = 'withIcon',
      required,
      selectOnFocus = true,
      size = 'small',
      usersOnly,
      value: valueProp,
      ...props
    },
    ref
  ) => {
    const elements = Array.isArray(valueProp) ? valueProp : [{ id: valueProp || '', type: 'User' }]
    const unifiedElements = elements.map(el => {
      if (typeof el === 'string') {
        return { id: el || '', type: 'User' }
      }

      return el
    })
    const userElements = unifiedElements?.filter(val => val.type === 'User') || []
    const groupElements = unifiedElements?.filter(val => val.type === 'Group') || []

    const { data } = useDefaultUserQuery({
      variables: {
        userIds: userElements.map(u => u.id) || [],
        groupIds: groupElements.map(g => g.id) || [],
      },
      skip: !valueProp?.length,
    })
    const { groups: searchGroups, searchTransfixAdminUsers } = data || {}
    const { nodes: users } = searchTransfixAdminUsers || {}
    const { nodes: groups } = searchGroups || {}
    const [inputValue, setInputValue] = useAutocompleteInputState('')
    const [refetch, { options = [], loading }] = useAutocompleteLazyQuery(
      useUsersLazyQuery,
      {
        variables: { searchString: inputValue },
      },
      {
        value: valueProp || null,
        getOptionsConfig: data => {
          const { groups: searchGroups, searchTransfixAdminUsers } = data || {}
          const { nodes: userNodes } = searchTransfixAdminUsers || {}
          const { nodes: groupNodes } = searchGroups || {}

          const usersGroup = {
            groupIndex: 1,
            groupName: 'Users',
            dividerAfter: true,
            options: compact([...(userNodes || []), ...(users || [])]),
            mapOption: ({ fullName, id }: ISearchUserFragment) => {
              return {
                id,
                text: fullName || '',
              }
            },
          }

          const groupsGroup = {
            groupIndex: 2,
            groupName: 'Groups',
            dividerAfter: true,
            options: compact([...(groupNodes || []), ...(groups || [])]),
            mapOption: ({ id, name }: ISearchGroupFragment) => {
              return {
                id,
                text: name || '',
              }
            },
          }

          return [usersGroup, ...(usersOnly ? [] : [groupsGroup])]
        },
      }
    )

    /**
     * Refetch when the input value changes
     */
    useDebounce(
      () => {
        if (inputValue) {
          refetch({ variables: { searchString: inputValue } })
        }
      },
      250,
      [inputValue]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
      if (isAutocompleteBaseOption(newVal)) {
        if (usersOnly) {
          onChange(newVal.id)
        } else {
          onChange({
            id: newVal.id,
            type: groupNameToType[newVal?.groupName || 'Users'] || 'User',
          })
        }
      } else if (Array.isArray(newVal)) {
        const values = newVal.filter(val =>
          isAutocompleteBaseOption(val)
        ) as IAutocompleteBaseOption[]

        if (!usersOnly) {
          onChange(
            values.map(({ groupName, id }) => ({
              id,
              type: groupNameToType[groupName || 'Users'] || 'User',
            }))
          )
        } else {
          onChange(values.map(({ id }) => id))
        }
      } else {
        onChange(null)
      }
    }

    const inputChangeHandler: TInputChangeHandler = (e, newVal) => {
      /**
       * MUI triggers an onChange event with `null` value when
       * trying to press delete/backspace when there's a value
       *
       * Because this is an invalid event, we ignore it
       */
      if (e) {
        setInputValue(e, newVal)
      }
    }

    /**
     * Set input value by default
     */
    useEffect(() => {
      if (!inputValue && users?.length === 1 && !multiple) {
        setInputValue({} as any, users[0]?.fullName || '')
      }
    }, [inputValue, setInputValue, valueProp, users, multiple])

    const value = getComputedValue(options, valueProp) || (multiple ? [] : null)

    return (
      <AutocompleteBase
        {...props}
        multiple={multiple}
        data-test={dataTest}
        ref={ref}
        value={value}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        options={options}
        loading={loading}
        loadingText='Loading...'
        selectOnFocus={selectOnFocus}
        autoSelect={autoSelect}
        clearOnBlur={clearOnBlur}
        renderInputType={renderInputType}
        inputIcon={inputIcon}
        size={size}
        required={required}
        inputError={inputError}
        inputValidationMessage={inputValidationMessage}
        filterOptions={node => node}
      />
    )
  }
)
